package components.project.configuration.layout.moduleLayout.planning

import com.meistercharts.charts.pv.modulePlanning.PvModulePlanningGestalt
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningLayer
import com.meistercharts.charts.pv.roofPlanning.PvRoofPlanningModel
import com.meistercharts.react.meistercharts
import it.neckar.commons.kotlin.js.safeGet
import it.neckar.lizergy.model.configuration.moduleLayout.planning.PvRoofPlanningModelInformation
import it.neckar.lizergy.model.configuration.moduleLayout.planning.toRestApiModel
import it.neckar.open.collections.fastForEach
import it.neckar.react.common.form.*
import it.neckar.react.common.form.EditableStatus.*
import kotlinx.atomicfu.atomic
import react.*

/**
 * Creates a MeisterCharts module area planning element
 */
fun RBuilder.moduleAreaPlanning(
  model: PvRoofPlanningModelInformation,
  editableStatus: EditableStatus,
  changeListener: (updatedModel: PvRoofPlanningModelInformation) -> Unit,
): Unit = child(ModuleAreaPlanning) {
  attrs {
    this.model = model
    this.changeListener = changeListener
    this.editableStatus = editableStatus
  }
}

val ModuleAreaPlanning: FC<ModuleAreaPlanningProps> = fc("ModuleAreaPlanning") { props ->
  //The React model (immutable)
  val model = props::model.safeGet()
  val changeListener = props::changeListener.safeGet()
  val editableStatus = props::editableStatus.safeGet()

  /**
   * Set to true if the mutable model is updated from the React model.
   * Avoid updating the React model if this is true - to avoid infinite loops
   */
  val updatingMutableModelFromReact = useMemo {
    atomic(false)
  }

  //The model that is used for meistercharts (and updated by meistercharts)
  val meisterchartsMutableModel = useMemo {
    PvRoofPlanningModel().also { mutableModel ->
      //Apply the loaded React model initially!
      model.applyToMutableModel(mutableModel)

      //Publish the changes from the meistercharts model to the React model
      val publishChanges: (newValue: Any?) -> Unit = {
        if (updatingMutableModelFromReact.value.not()) {
          //Only call change listeners if the model is not updated from the React model
          changeListener(mutableModel.toRestApiModel())
        }
      }

      /**
       * Register the location listeners for unusable areas
       */
      mutableModel.unusableAreas.unusableAreasProperty.consume(action = publishChanges)
      mutableModel.unusableAreas.unusableAreasProperty.consumeImmediately { newUnusableAreas ->
        newUnusableAreas.fastForEach {
          it.locationProperty.consume(action = publishChanges)
          it.sizeProperty.consume(action = publishChanges)
          it.rightTriangleTypeProperty.consume(action = publishChanges)
        }
      }

      mutableModel.moduleAreas.moduleAreasProperty.consume(action = publishChanges)
      mutableModel.moduleAreas.moduleAreasProperty.consumeImmediately { newModuleAreas ->
        newModuleAreas.fastForEach {
          it.locationProperty.consume(action = publishChanges)
          it.sizeProperty.consume(action = publishChanges)
          it.moduleOrientationProperty.consume(action = publishChanges)

          it.modules.visibleModulesProperty.consume(action = publishChanges)
          //TODO: Verify if this consumeImmediately registers a new listener for every change on every deleteProperty (hopefully it doesn't)
          it.modules.visibleModulesProperty.consumeImmediately { modules ->
            modules.fastForEach { module ->
              module.deletedProperty.consume(action = publishChanges)
            }
          }
        }
      }
    }
  }

  val roofPlanningGestalt: PvModulePlanningGestalt = useMemo {
    PvModulePlanningGestalt(meisterchartsMutableModel).also {
      it.mode = when (editableStatus) {
        Editable -> PvRoofPlanningLayer.Mode.Planning
        ReadOnly -> PvRoofPlanningLayer.Mode.Rendering
      }
    }
  }

  //Apply the changes
  updatingMutableModelFromReact.value = true
  try {
    meisterchartsMutableModel.roofSize = model.roofSize
    meisterchartsMutableModel.modulesSize = model.moduleSize
    meisterchartsMutableModel.suggestedRoofInsets = model.suggestedRoofInsets

    //do *NOT* updated the modules and unusable areas
    //These are only edited within meistercharts (at the moment).
    meisterchartsMutableModel.unusableAreas.unusableAreas.fastForEach { old ->
      model.unusableAreasInformation.unusableAreas.find { it.uuid == old.uuid }?.let { new ->
        old.location = new.location
        old.size = new.size
        old.description = new.description
      }
    }
    meisterchartsMutableModel.updateModuleAreas(model.moduleSize)

    roofPlanningGestalt.mode = when (editableStatus) {
      Editable -> PvRoofPlanningLayer.Mode.Planning
      ReadOnly -> PvRoofPlanningLayer.Mode.Rendering
    }
  } finally {
    updatingMutableModelFromReact.value = false
  }


  meistercharts(
    description = "Roof Planner",
    gestalt = roofPlanningGestalt,
    // Always mark as dirty on rerender. MeisterCharts is fast!
    callMarkAsDirty = true,
    width = "100%",
    height = "300px",
  )

}


external interface ModuleAreaPlanningProps : Props {
  var model: PvRoofPlanningModelInformation
  var changeListener: (updatedModel: PvRoofPlanningModelInformation) -> Unit
  var editableStatus: EditableStatus
}
